import { type ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { useMutation } from '~/hooks/use-mutation'
import { useToast } from '~/hooks/use-toast'
import { MutationKey } from '~/types/mutation-key'
import type { SchoolImportResult } from '~/types/school'

const CSV_EXTENSION = '.csv'

const TEMPLATE_FILENAME = 'schools-import-template.csv'
// BOM so Excel opens the Japanese column correctly; example rows show the
// expected shape and are meant to be replaced by the real data.
const TEMPLATE_CSV = '﻿nameEn,nameJa\nThe University of Tokyo,東京大学\nWaseda University,早稲田大学\n'

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = TEMPLATE_FILENAME
  anchor.click()
  URL.revokeObjectURL(url)
}

type SchoolCsvImportFormProps = {
  onImported: (result: SchoolImportResult) => void
}

/**
 * Bulk school import: pick a CSV (`nameEn,nameJa` columns), upload it to
 * `POST /admin/schools/import`, and hand the server's per-row result to the
 * parent (`SchoolImportDialog`), which renders it and decides whether to
 * expand the dialog. The file is plain component state (like the skill icon
 * picker) since it is uploaded directly rather than tracked through
 * react-hook-form. Rendered fresh every time the import dialog opens, so
 * this component's own state never needs an explicit reset.
 */
export function SchoolCsvImportForm({ onImported }: SchoolCsvImportFormProps) {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)

  const { mutate: importSchools, isPending } = useMutation<SchoolImportResult, FormData>({
    mutationKey: MutationKey.IMPORT_SCHOOLS_CSV,
    method: 'POST',
    options: {
      onSuccess: (data) => {
        showSuccess(t('schools.importComplete'))
        setFile(null)
        if (inputRef.current) inputRef.current.value = ''
        onImported(data)
      },
    },
  })

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null

    if (selected && !selected.name.toLowerCase().endsWith(CSV_EXTENSION)) {
      showError(t('schools.invalidFileType'))
      event.target.value = ''
      setFile(null)
      return
    }

    setFile(selected)
  }

  const handleClearFile = () => {
    setFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleUpload = () => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    importSchools(formData)
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('schools.importDescription')}</p>
      <div className="flex items-center gap-3">
        <Input
          ref={inputRef}
          id="schools-csv-file"
          type="file"
          accept=".csv,text/csv"
          disabled={isPending}
          onChange={handleFileChange}
          className="max-w-sm"
        />
        <Button id="schools-import-button" onClick={handleUpload} disabled={!file} loading={isPending}>
          {t('schools.upload')}
        </Button>
        {file && (
          <Button variant="outline" type="button" onClick={handleClearFile} disabled={isPending}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
      {file && <p className="text-sm text-muted-foreground">{file.name}</p>}
      <Button
        id="schools-template-download-button"
        variant="link"
        type="button"
        onClick={downloadTemplate}
        className="h-auto p-0"
      >
        {t('schools.downloadTemplate')}
      </Button>
    </div>
  )
}
